import Parse from 'parse'
import { getServerBaseUrl } from '@/utils/serverUrl'

interface User {
  id: string
  username: string
  email: string
  role: string
  isPlatformAdmin: boolean
  createdAt: string
  updatedAt: string
}

interface Company {
  id: string
  name: string
  description: string
  status: string
  createdAt: string
  updatedAt: string
}

interface BillingTier {
  id: string
  name: string
  description: string
  price: number
  features: string[]
  order: number
  isActive: boolean
  createdAt: string
  updatedAt: string
}

interface AdminStatus {
  success: boolean
  status: string
  timestamp: string
  statistics: {
    totalUsers: number
    totalCompanies: number
    totalBillingTiers: number
  }
  admin: {
    userId: string
    role: string
  }
}

interface UsersResponse {
  success: boolean
  users: User[]
  pagination: {
    limit: number
    skip: number
    total: number
  }
}

/** Query params for GET /api/admin/users (global admin). */
export interface GetUsersOptions {
  limit?: number
  skip?: number
  search?: string
  /** Omit or 'all' on the client means do not send role (server lists all roles). */
  role?: string
  isPlatformAdmin?: boolean
  createdFrom?: string
  createdTo?: string
  companyId?: string
  noCompany?: boolean
}

interface CompaniesResponse {
  success: boolean
  companies: Company[]
  pagination: {
    limit: number
    skip: number
    total: number
  }
}

interface BillingTiersResponse {
  success: boolean
  billingTiers: BillingTier[]
}

export interface TransactionalEmailTemplateMeta {
  key: string
  description: string
  category: string
}

export interface TransactionalEmailFilterMetaResponse {
  success: boolean
  categories: string[]
  templates: TransactionalEmailTemplateMeta[]
}

export interface TransactionalEmailAuditRow {
  id: string
  sentAt: string
  status: string
  category: string
  templateName: string
  eventType: string
  recipientEmail: string
  subject: string
  companyId?: string
  userId?: string
  messageId?: string
  error?: string
  sentBy?: string
  auditTransport?: string
}

export interface TransactionalEmailsListResponse {
  success: boolean
  rows: TransactionalEmailAuditRow[]
  total: number
  pagination: { limit: number; skip: number }
}

export interface TransactionalEmailsQuery {
  limit?: number
  skip?: number
  companyId?: string
  userId?: string
  category?: string
  templateName?: string
  status?: 'success' | 'failed'
  from?: string
  to?: string
  q?: string
  /** Include historical EmailLog rows not written by the Node transactional mailer. */
  includeLegacyEmailLog?: boolean
  /** Include dev console-only rows (EMAIL_LOG_ONLY), hidden by default. */
  includeDevLogOnly?: boolean
}

interface SystemHealthResponse {
  success: boolean
  system: {
    uptime: number
    memory: {
      heapUsed: string
      heapTotal: string
      external: string
      rss: string
    }
    nodeVersion: string
    platform: string
    pid: number
  }
  admin: {
    userId: string
    role: string
    isPlatformAdmin: boolean
  }
}

/**
 * A refused admin call. The message stays `HTTP error! status: N` as before; the server's sentence
 * and code (e.g. 409 `last_owner`) are kept apart so a screen can explain the refusal.
 */
export class AdminHttpError extends Error {
  readonly status: number
  readonly code?: string
  readonly serverMessage?: string

  constructor(status: number, body: { error?: unknown; code?: unknown } | null) {
    super(`HTTP error! status: ${status}`)
    this.name = 'AdminHttpError'
    this.status = status
    const text = (value: unknown) => (typeof value === 'string' && value ? value : undefined)
    this.code = text(body?.code)
    this.serverMessage = text(body?.error)
  }
}

class AdminService {
  private serverBaseUrl: string

  constructor() {
    this.serverBaseUrl = getServerBaseUrl()
  }

  /**
   * Get authentication headers for API requests
   */
  private async getAuthHeaders(): Promise<Record<string, string>> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    }

    try {
      const currentUser = Parse.User.current()
      if (currentUser) {
        const sessionToken = currentUser.getSessionToken()
        if (sessionToken) {
          headers['Authorization'] = `Bearer ${sessionToken}`
        }
      }
    } catch (error) {
      console.warn('Failed to get session token for admin request:', error)
    }

    return headers
  }

  /**
   * Get admin system status
   */
  async getStatus(): Promise<AdminStatus> {
    const headers = await this.getAuthHeaders()
    const response = await fetch(`${this.serverBaseUrl}/api/admin/status`, {
      method: 'GET',
      headers,
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    return response.json()
  }

  /**
   * Get users with pagination and optional filters (global admin).
   */
  async getUsers(options: GetUsersOptions = {}): Promise<UsersResponse> {
    const headers = await this.getAuthHeaders()
    const limit = options.limit ?? 50
    const skip = options.skip ?? 0
    const params = new URLSearchParams({
      limit: String(limit),
      skip: String(skip),
    })
    if (options.search) params.set('search', options.search)
    if (options.role && options.role !== 'all') params.set('role', options.role)
    if (options.isPlatformAdmin !== undefined) {
      params.set('isPlatformAdmin', options.isPlatformAdmin ? 'true' : 'false')
    }
    if (options.createdFrom) params.set('createdFrom', options.createdFrom)
    if (options.createdTo) params.set('createdTo', options.createdTo)
    if (options.companyId) params.set('companyId', options.companyId)
    if (options.noCompany) params.set('noCompany', 'true')

    const response = await fetch(`${this.serverBaseUrl}/api/admin/users?${params}`, {
      method: 'GET',
      headers,
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    return response.json()
  }

  /**
   * Get all companies with pagination and search
   */
  async getCompanies(limit: number = 50, skip: number = 0, search?: string): Promise<CompaniesResponse> {
    const headers = await this.getAuthHeaders()
    const params = new URLSearchParams({
      limit: limit.toString(),
      skip: skip.toString(),
      ...(search && { search })
    })

    const response = await fetch(`${this.serverBaseUrl}/api/admin/companies?${params}`, {
      method: 'GET',
      headers,
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    return response.json()
  }

  /**
   * Get all billing tiers
   */
  async getBillingTiers(): Promise<BillingTiersResponse> {
    const headers = await this.getAuthHeaders()
    const response = await fetch(`${this.serverBaseUrl}/api/admin/billing-tiers`, {
      method: 'GET',
      headers,
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    return response.json()
  }

  /**
   * Update user role
   */
  async updateUserRole(userId: string, role: string, isPlatformAdmin?: boolean): Promise<{ success: boolean; message: string; user: any }> {
    const headers = await this.getAuthHeaders()
    const response = await fetch(`${this.serverBaseUrl}/api/admin/users/${userId}/role`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ role, isPlatformAdmin }),
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    return response.json()
  }

  /**
   * Get user deletion preview
   */
  async getUserDeletionPreview(userId: string): Promise<any> {
    const headers = await this.getAuthHeaders()
    const response = await fetch(`${this.serverBaseUrl}/api/admin/user-deletion-preview`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ user_id: userId }),
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    return response.json()
  }

  /**
   * Delete user completely
   */
  async deleteUser(userId: string, reason?: string): Promise<any> {
    const headers = await this.getAuthHeaders()
    const response = await fetch(`${this.serverBaseUrl}/api/admin/delete-user`, {
      method: 'DELETE',
      headers,
      body: JSON.stringify({ user_id: userId, reason }),
    })

    if (!response.ok) {
      // A non-JSON body (a proxy error page) just means no sentence or code.
      const body = await response.json().catch(() => null)
      throw new AdminHttpError(response.status, body && typeof body === 'object' ? body : null)
    }

    return response.json()
  }

  /**
   * Get system health information
   */
  async getSystemHealth(): Promise<SystemHealthResponse> {
    const headers = await this.getAuthHeaders()
    const response = await fetch(`${this.serverBaseUrl}/api/admin/system-health`, {
      method: 'GET',
      headers,
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    return response.json()
  }

  async getTransactionalEmailFilters(): Promise<TransactionalEmailFilterMetaResponse> {
    const headers = await this.getAuthHeaders()
    const response = await fetch(`${this.serverBaseUrl}/api/admin/transactional-email-filters`, {
      method: 'GET',
      headers,
    })
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }
    return response.json()
  }

  async getTransactionalEmails(query: TransactionalEmailsQuery): Promise<TransactionalEmailsListResponse> {
    const headers = await this.getAuthHeaders()
    const params = new URLSearchParams()
    if (query.limit != null) params.set('limit', String(query.limit))
    if (query.skip != null) params.set('skip', String(query.skip))
    if (query.companyId) params.set('companyId', query.companyId)
    if (query.userId) params.set('userId', query.userId)
    if (query.category) params.set('category', query.category)
    if (query.templateName) params.set('templateName', query.templateName)
    if (query.status) params.set('status', query.status)
    if (query.from) params.set('from', query.from)
    if (query.to) params.set('to', query.to)
    if (query.q) params.set('q', query.q)
    if (query.includeLegacyEmailLog) params.set('includeLegacyEmailLog', '1')
    if (query.includeDevLogOnly) params.set('includeDevLogOnly', '1')

    const response = await fetch(`${this.serverBaseUrl}/api/admin/transactional-emails?${params}`, {
      method: 'GET',
      headers,
    })
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }
    return response.json()
  }
}

// Export singleton instance
export const adminService = new AdminService()
export default adminService
